import * as Sentry from '@sentry/react'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

type GetNewsAttachmentUrlProps = {
  newsId: string
  fileName: string
}

type GetNewsAttachmentUrlResponse = {
  fileUrl: string
}

async function getNewsAttachmentUrl({
  newsId,
  fileName,
}: GetNewsAttachmentUrlProps) {
  const { data } = await api.backend.get<GetNewsAttachmentUrlResponse>(
    `/posts/get-post-file/${newsId}`,
    {
      params: {
        fileName,
      },
    },
  )

  return data
}

export function useDownloadNewsAttachment() {
  const { t } = useTranslation('panel')

  const handleDownloadNewsAttachment = async ({
    newsId,
    fileName,
  }: Partial<GetNewsAttachmentUrlProps>) => {
    if (!newsId || !fileName) {
      Sentry.captureMessage(
        `Unable to download news attachment. News ID: ${newsId}.`,
        'error',
      )
      return
    }

    await queryClient
      .fetchQuery<GetNewsAttachmentUrlResponse>({
        queryKey: ['/posts/get-post-file', newsId, fileName],
        queryFn: () => getNewsAttachmentUrl({ newsId, fileName }),
      })
      .then((data) => window.open(data.fileUrl, '_blank'))
      .catch((err) => {
        Sentry.captureException(err)

        toast(Toast, {
          data: {
            type: 'danger',
            icon: ServerCrash,
            title: t('news.use_download_news_attachment.toast_error.title'),
            description: t(
              'news.use_download_news_attachment.toast_error.description',
            ),
          },
        })
      })
  }

  return {
    handleDownloadNewsAttachment,
  }
}
